import { isCallable } from '@afterpay/utils'
import clsx, { ClassValue } from 'clsx'
import React, { FunctionComponent, ReactNode, SyntheticEvent, useContext, useEffect } from 'react'
import { Modal as ReactModal } from 'react-overlays'
import { BaseModalProps, RenderModalBackdropProps } from 'react-overlays/Modal'

import { Button } from '../Button'
import { Divider } from '../Divider'
import { ColorTypesType } from '../foundation/Colors'
import { Icon } from '../Icon'
import { Loader } from '../Loader'
import { useThemeClass } from '../Theme'
import { useBreakpoint } from '../useBreakpoint'
import { createUseFeatureDeepLink } from '../useFeatureDeepLink'
import { BACKDROP_KIND } from './constant'
import { ModalContext, registerModal } from './modalContext'
import styles from './style.module.scss'
import { Transition } from './Transition'

type DeprecatedBackgroundColorTypes = Pick<ColorTypesType, 'WHITE' | 'BONDI_MINT'>
type DeprecatedBackgroundColorKeys = keyof DeprecatedBackgroundColorTypes
type DeprecatedBackgroundColor = DeprecatedBackgroundColorTypes[DeprecatedBackgroundColorKeys]
type SemanticBackgroundColor = 'Default' | 'Alternate'
type BackgroundColor = DeprecatedBackgroundColor | SemanticBackgroundColor

type BackdropKindTypes = typeof BACKDROP_KIND
type BackdropKindKeys = keyof BackdropKindTypes
type BackdropKind = BackdropKindTypes[BackdropKindKeys]

type RenderMode = 'FullScreen' | 'Native'
type Size = 'Small' | 'Medium' | 'Large'

const mapDeprecatedToSemanticBackgroundColor: Record<DeprecatedBackgroundColor, SemanticBackgroundColor> = {
  White: 'Default',
  'Bondi Mint': 'Alternate',
}

const getSemanticBackgroundColor = (color: string): SemanticBackgroundColor => {
  return mapDeprecatedToSemanticBackgroundColor[color as DeprecatedBackgroundColor] || color
}

export interface ModalProps extends Omit<BaseModalProps, 'transition' | 'renderBackdrop' | 'children'> {
  id?: string
  onClose?: (e?: SyntheticEvent) => void
  children: ReactNode
  testNameSpace?: string
  closeButtonAriaLabel?: string
  showCloseButton?: boolean
  noPadding?: boolean
  backgroundColor?: BackgroundColor
  backdropKind?: BackdropKind
  loading?: boolean
  horizontalScroll?: boolean
  animate?: boolean
  /**
   * @deprecated use `renderMode` instead
   * renderMode = FullScreen is the same as fullScreen = true
   */
  fullScreen?: boolean
  renderMode?: RenderMode
  size?: Size
}

export interface ModalHeaderProps {
  divider?: boolean
  children: ReactNode
}

export interface ModelFooterProps {
  children: ReactNode
}

export interface ModelContentProps {
  children: ReactNode
}

export type ModalComponent = FunctionComponent<ModalProps> & {
  Header: FunctionComponent<ModalHeaderProps>
  Footer: FunctionComponent<ModelFooterProps>
  Content: FunctionComponent<ModelContentProps>
  Loader: FunctionComponent<{ isOverlay?: boolean }>
}

export const Modal: ModalComponent = ({
  id,
  show = false,
  testNameSpace = '',
  closeButtonAriaLabel = '',
  children,
  onClose,
  onShow = () => {},
  backdrop = 'static',
  onBackdropClick = () => {},
  showCloseButton = true,
  noPadding = false,
  backgroundColor = 'White',
  backdropKind = 'Default',
  loading = false,
  horizontalScroll = true,
  animate = true,
  renderMode,
  size = 'Small',
  ...otherProps
}) => {
  const { dispatch } = useContext(ModalContext)

  const { screenSize } = useBreakpoint()
  const themeClass = useThemeClass()

  const finalBackgroundColor = getSemanticBackgroundColor(backgroundColor)

  const renderDrawer = (): ClassValue | undefined => {
    if (renderMode === 'Native') {
      return styles.native
    }

    if (screenSize === 'Small') {
      return renderMode === 'FullScreen' ? styles.fullScreen : styles.drawer
    }

    return undefined
  }

  const shouldApplyMargin = screenSize !== 'Small' && renderMode !== 'FullScreen' && renderMode !== 'Native'

  const modalStyle = clsx(
    styles.modal,
    styles[`size${size}`],
    renderDrawer(),
    shouldApplyMargin && styles.largeScreenMargin,
    noPadding && styles.noPadding,
    finalBackgroundColor === 'Alternate' && styles['bg-alternate'],
    !horizontalScroll && styles.horizontalScroll,
    animate && styles.transition,
    styles[themeClass]
  )

  const backdropStyle = clsx(styles.backdrop, backdropKind === 'Darker' && styles['opacity-90'])

  const renderBackdrop = (props: RenderModalBackdropProps): ReactNode => {
    const backdropNameSpace = testNameSpace !== '' ? `${testNameSpace}-backdrop` : 'backdrop'
    if (show) {
      return <div className={backdropStyle} {...props} data-testid={backdropNameSpace} />
    }
    return null
  }

  const renderCloseButton = (): ReactNode => {
    if (!isCallable(onClose)) {
      return
    }
    const buttonTestNameSpace = testNameSpace !== '' ? `${testNameSpace}-close-modal` : 'close-modal'
    return (
      <div className={styles.close}>
        <Button.Link
          type='button'
          size='Regular'
          testNameSpace={buttonTestNameSpace}
          aria-label={closeButtonAriaLabel.length === 0 ? 'Close' : closeButtonAriaLabel}
          onClick={onClose}
        >
          <Icon.Close size='Small' />
        </Button.Link>
      </div>
    )
  }

  /**
   * This is for the `theme-color` attribute of the page when the modal is shown.
   * Due to the unsupport of a transparent color, set the black color as the default
   */
  useEffect(() => {
    const color = document.querySelector('meta[name="theme-color"]')?.getAttribute('content') ?? '#FFFFFF'
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', show ? '#000000' : color)
  }, [show])

  useEffect(() => {
    if (id) {
      dispatch(registerModal(id))
    }
  }, [id, dispatch])

  return (
    <ReactModal
      show={show}
      onClose={onClose}
      onShow={onShow}
      className={modalStyle}
      renderBackdrop={renderBackdrop}
      onBackdropClick={onBackdropClick}
      aria-labelledby='modal-label'
      transition={Transition}
      backdrop={backdrop}
      {...(testNameSpace.length > 0 && {
        'data-testid': `${testNameSpace}-modal`,
      })}
      {...otherProps}
    >
      <>
        {loading && <ModalLoader />}
        {showCloseButton && renderCloseButton()}
        <div aria-hidden={loading}>{children}</div>
      </>
    </ReactModal>
  )
}

const ModalHeader: FunctionComponent<ModalHeaderProps> = ({ divider = false, children }) => (
  <>
    <div className={styles.header}>{children}</div>
    {divider && (
      <div className={styles.divider}>
        <Divider testNameSpace='modal-header' kind='Hairline' />
      </div>
    )}
  </>
)

const ModalFooter: FunctionComponent<ModelFooterProps> = ({ children }) => {
  return <div className={styles.footer}>{children}</div>
}

const ModalContent: FunctionComponent<ModelContentProps> = ({ children }) => {
  return <div className={styles.body}>{children}</div>
}

const ModalLoader: FunctionComponent<{ isOverlay?: boolean }> = ({ isOverlay = true }) => {
  return (
    <div className={isOverlay ? styles.loaderOverlay : undefined}>
      <div className={styles.loader}>
        <Loader pulse={false} />
      </div>
    </div>
  )
}

Modal.Header = ModalHeader
Modal.Footer = ModalFooter
Modal.Content = ModalContent
Modal.Loader = ModalLoader

/**
 * Registers a component to update the browser location search to reflect the display status of a modal.
 * Enables deeplinking to modals and preserving modal display status across page refreshes.
 *
 * @param {string} value - The display status of the modal
 * @param {string} modifier  - The unique identifier of the modal
 * @param {boolean} ignoreFirstRender - Whether to ignore the first render
 * @returns {void}
 * @example
 * import { useModalDeepLink } from '@afterpaytouch/core'
 *
 * useModalDeepLink('true', 'apm') // Updates the browser location search to `?modal-apm=true`
 */
export const useModalDeepLink = createUseFeatureDeepLink('modal')
